import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../rbac';
import BusinessTaxDeclarations from '../../components/BusinessTaxDeclarations';
import { LocationPicker } from '../../components/maps/LocationMap';
import {
  businessAPI, contentAPI, identityAPI, propertyAPI, userApprovalAPI,
} from '../../services/api';
import {
  BUSINESS_TYPES, CONSTITUTION_LABELS, CONSTITUTION_TYPES, GRANT_SCOPES,
  OCCUPANCY_TYPES, PARTY_ROLE_TYPES, READINESS_LABELS, ROLE_LABELS,
  STATUS_LABELS, TYPE_LABELS, activeRecords, cleanBusinessPayload,
  cleanLocationPayload, cleanRolePayload, clientReadiness, emptyBusiness,
  emptyLocation, emptyRole, errorMessage,
  isUnregisteredBusiness, todayIso,
} from './businessRegistryUi';
import '../../components/form/FormStyles.css';
import './AdminBusinessForm.css';

const CORE_KEYS = Object.keys(emptyBusiness());
const equityRole = roleType => ['proprietor', 'partner'].includes(roleType);
const localKey = prefix => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const Field = ({ label, name, value, onChange, type = 'text', required = false, optional = false, isInvalid = false, errorHint, help, ...props }) => (
  <label className="registry-field" htmlFor={name}>
    <span>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </span>
    <input
      id={name}
      name={name}
      type={type}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      className={isInvalid ? 'invalid-field' : ''}
      {...props}
    />
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : help ? <small>{help}</small> : null}
  </label>
);

const SelectField = ({ label, name, value, onChange, options, required = false, optional = false, isInvalid = false, errorHint, placeholder, disabled = false }) => (
  <label className="registry-field" htmlFor={name}>
    <span>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </span>
    <select
      id={name}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      disabled={disabled}
      className={isInvalid ? 'invalid-field' : ''}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(option => {
        const item = typeof option === 'string' ? { value: option, label: option.replaceAll('_', ' ') } : option;
        return <option key={item.value} value={item.value}>{item.label}</option>;
      })}
    </select>
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : null}
  </label>
);

const Checkbox = ({ label, checked, onChange, disabled = false }) => (
  <label className="registry-checkbox"><input type="checkbox" checked={Boolean(checked)} onChange={onChange} disabled={disabled} /><span>{label}</span></label>
);

function PartyFinder({ tenantSlug, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newParty, setNewParty] = useState({
    party_type: 'individual', legal_name: '', display_name: '', primary_phone: '',
    primary_email: '', profile_kind: 'resident',
  });

  const search = async () => {
    setLoading(true); setError('');
    try {
      const q = query.trim() || undefined;
      const cleanPhone = (p) => String(p || '').replace(/\D/g, '').slice(-10);

      const [partyRes, userRes] = await Promise.all([
        identityAPI.searchParties(tenantSlug, { q, limit: 20 }).catch(() => ({ data: { parties: [] } })),
        userApprovalAPI.getAll(tenantSlug, { status: 'approved', search: q, limit: 20 }).catch(() => ({ data: { users: [] } })),
      ]);

      const parties = (partyRes.data?.parties || []).map(p => ({
        sourceType: 'party',
        party_uid: p.party_uid,
        display_name: p.display_name || p.legal_name,
        legal_name: p.legal_name,
        phone: p.primary_phone,
        email: p.primary_email,
        badge: 'नोंदणीकृत पक्ष',
        subtext: `${p.party_type === 'individual' ? 'नागरिक' : 'संस्था'} · ${p.primary_phone || p.party_uid}`,
        raw: p,
      }));

      const users = (userRes.data?.users || []).map(u => ({
        sourceType: 'user',
        user_id: u.id,
        party_uid: u.party_uid || null,
        display_name: u.full_name,
        legal_name: u.full_name,
        phone: u.phone,
        email: u.email,
        badge: 'पोर्टल नागरिक',
        subtext: `नागरिक वापरकर्ता · फोन: ${u.phone || '—'} · ID: ${u.id}`,
        raw: u,
      }));

      // Deduplicate: Map parties by phone to remove duplicate party records
      const partyByPhone = new Map();
      const nonPhoneParties = [];
      for (const p of parties) {
        const phone = cleanPhone(p.phone);
        if (phone) {
          if (!partyByPhone.has(phone)) {
            partyByPhone.set(phone, p);
          }
        } else {
          nonPhoneParties.push(p);
        }
      }

      const deduplicated = [];
      const handledPhones = new Set();

      // For users: if a matching party exists, fuse them into one unified entity
      for (const u of users) {
        const phone = cleanPhone(u.phone);
        if (phone && partyByPhone.has(phone)) {
          const matched = partyByPhone.get(phone);
          deduplicated.push({
            sourceType: 'party',
            party_uid: matched.party_uid,
            display_name: u.display_name || matched.display_name,
            legal_name: u.legal_name || matched.legal_name,
            phone: u.phone || matched.phone,
            email: u.email || matched.email,
            badge: 'नागरिक (नोंदणीकृत)',
            subtext: `पोर्टल नागरिक · फोन: ${u.phone || matched.phone || '—'} · ID: ${u.id}`,
            raw: matched.raw,
          });
          handledPhones.add(phone);
        } else {
          if (phone) handledPhones.add(phone);
          deduplicated.push(u);
        }
      }

      // Add remaining parties that didn't match any portal user
      for (const [phone, p] of partyByPhone.entries()) {
        if (!handledPhones.has(phone)) {
          deduplicated.push(p);
        }
      }
      for (const p of nonPhoneParties) {
        deduplicated.push(p);
      }

      setResults(deduplicated);
    } catch (requestError) {
      setError(errorMessage(requestError, 'पक्ष किंवा वापरकर्ते शोधता आले नाहीत.'));
    } finally { setLoading(false); }
  };

  const selectItem = async (item) => {
    if (item.sourceType === 'party') {
      onSelect(item.raw);
    } else if (item.party_uid) {
      onSelect({
        party_uid: item.party_uid,
        legal_name: item.legal_name,
        display_name: item.display_name,
        primary_phone: item.phone,
        primary_email: item.email,
        party_type: 'individual',
      });
    } else {
      const cleanPhone = (p) => String(p || '').replace(/\D/g, '').slice(-10);
      const existingMatch = results.find(r => r.party_uid && r.phone && cleanPhone(r.phone) === cleanPhone(item.phone));
      if (existingMatch) {
        onSelect(existingMatch.raw || {
          party_uid: existingMatch.party_uid,
          legal_name: item.legal_name,
          display_name: item.display_name,
          primary_phone: item.phone,
          primary_email: item.email,
          party_type: 'individual',
        });
        return;
      }
      try {
        const res = await identityAPI.createParty(tenantSlug, {
          party_type: 'individual',
          legal_name: item.legal_name,
          display_name: item.display_name,
          primary_phone: item.phone,
          primary_email: item.email,
          profile_kind: 'resident',
        });
        onSelect(res.data.party);
      } catch {
        onSelect({
          party_uid: null,
          legal_name: item.legal_name,
          display_name: item.display_name,
          primary_phone: item.phone,
          primary_email: item.email,
          party_type: 'individual',
        });
      }
    }
  };

  const create = async () => {
    if (newParty.legal_name.trim().length < 2) return setError('कायदेशीर नाव आवश्यक आहे.');
    setLoading(true); setError('');
    try {
      const response = await identityAPI.createParty(tenantSlug, Object.fromEntries(
        Object.entries(newParty).map(([key, value]) => [key, typeof value === 'string' ? value.trim() || null : value])
      ));
      const party = response.data.party;
      onSelect(party);
      setResults(current => [{
        sourceType: 'party',
        party_uid: party.party_uid,
        display_name: party.display_name || party.legal_name,
        legal_name: party.legal_name,
        phone: party.primary_phone,
        badge: 'नवीन पक्ष',
        subtext: party.primary_phone || party.party_uid,
        raw: party,
      }, ...current]);
      setShowCreate(false);
    } catch (requestError) {
      setError(errorMessage(requestError, 'पक्ष तयार करता आला नाही.'));
    } finally { setLoading(false); }
  };

  return (
    <div className="registry-picker">
      <div className="registry-picker-search">
        <label><span className="sr-only">वापरकर्ता किंवा पक्ष शोधा</span><input value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); search(); } }} placeholder="नाव, फोन, ई-मेल किंवा User / Party ID ने शोधा..." /></label>
        <button type="button" onClick={search} disabled={loading}>{loading ? '...' : 'शोधा'}</button>
        <button type="button" className="secondary" onClick={() => setShowCreate(value => !value)}>+ नवीन मालक नोंदवा</button>
      </div>
      {error && <div className="registry-inline-error" role="alert">{error}</div>}
      {results.length > 0 && <div className="registry-picker-results" role="listbox" aria-label="शोध निकाल">
        {results.map((item, idx) => (
          <button type="button" key={item.party_uid || `user-${item.user_id}-${idx}`} onClick={() => selectItem(item)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', gap: '12px', textAlign: 'left' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <strong style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>{item.display_name || item.legal_name}</strong>
              <small style={{ fontSize: '12px', color: '#64748b' }}>{item.subtext}</small>
            </div>
            <span style={{ fontSize: '11px', whiteSpace: 'nowrap', background: item.badge?.includes('नागरिक') ? '#e0f2fe' : '#fef3c7', color: item.badge?.includes('नागरिक') ? '#0369a1' : '#92400e', padding: '4px 10px', borderRadius: '12px', fontWeight: 600 }}>
              {item.badge}
            </span>
          </button>
        ))}
      </div>}
      {showCreate && <div className="registry-inline-form">
        <h4>नवीन मालक / व्यक्ती तपशील</h4>
        <SelectField label="प्रकार" name="new-party-type" value={newParty.party_type} onChange={event => setNewParty({ ...newParty, party_type: event.target.value })} options={[{ value: 'individual', label: 'व्यक्ती / Individual' }, { value: 'organization', label: 'संस्था / Organization' }]} />
        <Field label="पूर्ण नाव *" name="new-party-legal" value={newParty.legal_name} onChange={event => setNewParty({ ...newParty, legal_name: event.target.value })} placeholder="उदा. रमेश पांडुरंग पाटील" />
        <Field label="प्रदर्शित नाव" name="new-party-display" value={newParty.display_name} onChange={event => setNewParty({ ...newParty, display_name: event.target.value })} />
        <Field label="मोबाईल नंबर" name="new-party-phone" value={newParty.primary_phone} onChange={event => setNewParty({ ...newParty, primary_phone: event.target.value })} inputMode="tel" placeholder="१० अंकी मोबाईल नंबर" />
        <Field label="ई-मेल" name="new-party-email" type="email" value={newParty.primary_email} onChange={event => setNewParty({ ...newParty, primary_email: event.target.value })} />
        <div className="registry-inline-actions"><button type="button" onClick={create} disabled={loading}>जतन करून मालक म्हणून निवडा</button><button type="button" className="secondary" onClick={() => setShowCreate(false)}>रद्द</button></div>
      </div>}
    </div>
  );
}

function PropertyFinder({ tenantSlug, excludeBusinessUid, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [state, setState] = useState({ loading: false, error: '' });

  const search = async () => {
    const q = query.trim();
    setState({ loading: true, error: '' });
    try {
      if (q) {
        try {
          const chk = await propertyAPI.checkMilkat(tenantSlug, {
            milkat_number: q,
            exclude_business_uid: excludeBusinessUid || undefined,
          });
          if (chk.data?.data?.business_duplicate) {
            const dupName = chk.data.data.business_duplicate.business_name;
            setState({
              loading: false,
              error: `⚠️ हा मिळकत क्रमांक (${q}) आधीच '${dupName}' या व्यवसायाशी जोडलेला आहे.`,
            });
          }
        } catch (_) {}
      }

      const response = await propertyAPI.getAll(tenantSlug, { search: q || undefined, limit: 20 });
      setResults(response.data.properties || []);
      setState(prev => ({ ...prev, loading: false }));
    } catch (requestError) {
      setState({ loading: false, error: errorMessage(requestError, 'मालमत्ता शोधता आली नाही.') });
    }
  };

  const handleSelect = async (property) => {
    if (property?.assessment_number) {
      setState({ loading: true, error: '' });
      try {
        const chk = await propertyAPI.checkMilkat(tenantSlug, {
          milkat_number: property.assessment_number,
          exclude_business_uid: excludeBusinessUid || undefined,
        });
        if (chk.data?.data?.business_duplicate) {
          const dupName = chk.data.data.business_duplicate.business_name;
          setState({
            loading: false,
            error: `⚠️ हा मिळकत क्रमांक (${property.assessment_number}) आधीच '${dupName}' या व्यवसायाशी जोडलेला आहे.`,
          });
          return;
        }
      } catch (_) {}
      setState({ loading: false, error: '' });
    }
    onSelect(property);
  };

  return <div className="registry-picker compact">
    <div className="registry-picker-search"><label><span className="sr-only">मालमत्ता शोधा</span><input value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); search(); } }} placeholder="मिळकत / असेसमेंट क्रमांक, मालमत्ता नाव, पत्ता" /></label><button type="button" onClick={search} disabled={state.loading}>{state.loading ? '...' : 'शोधा'}</button></div>
    {state.error && <div className="registry-inline-error" style={{ color: '#dc2626', fontWeight: 600 }}>{state.error}</div>}
    {results.length > 0 && <div className="registry-picker-results">{results.map(property => <button type="button" key={property.property_uid} onClick={() => handleSelect(property)}><strong>{property.property_name}</strong><small>मिळकत क्र: {property.assessment_number} · {[property.address_line_1, property.locality, property.ward_number].filter(Boolean).join(', ')}{property.primary_owner?.display_name ? ` · ${property.primary_owner.display_name}` : ''}</small></button>)}</div>}
  </div>;
}

const roleForEdit = role => ({
  ...role,
  party_uid: role.party_uid || role.party?.party_uid || '',
  ownership_share: role.ownership_share ?? '',
  valid_from: role.valid_from ? String(role.valid_from).slice(0, 10) : '',
  valid_to: role.valid_to ? String(role.valid_to).slice(0, 10) : '',
  evidence_note: role.evidence?.note || '',
  evidence_attestation: Boolean(role.evidence?.attestation),
});

const locationForEdit = location => ({
  ...emptyLocation(), ...location,
  property_uid: '',
  property_action: 'keep',
  milkat_number: location.milkat_number || location.Property?.assessment_number || (location.branch_name?.match(/मिळकत क्र\.\s*([^\s,]+)/)?.[1] || ''),
  valid_from: location.valid_from ? String(location.valid_from).slice(0, 10) : '',
  valid_to: location.valid_to ? String(location.valid_to).slice(0, 10) : '',
  floor_area_sqft: location.floor_area_sqft ?? '', signage_area_sqft: location.signage_area_sqft ?? '',
  latitude: location.latitude ?? '', longitude: location.longitude ?? '',
});

const displayDateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString('en-IN');
};

const roleChangeErrorMessage = (error, fallback) => {
  const message = errorMessage(error, fallback);
  if (error?.response?.status !== 409) return message;
  if (/pending/i.test(message)) {
    return `A pending role-set change already exists. Review or reject it before submitting another request. (${message})`;
  }
  if (/version|updated|changed|refresh|reviewed/i.test(message)) {
    return `This proposal is stale because the business or request changed. Reload the business and submit a fresh complete role set. (${message})`;
  }
  return `The role-set change conflicts with the current registry state. Reload and try again. (${message})`;
};

const roleSetKey = (role, index) => `${role.party_uid || 'party'}-${role.role_type || 'role'}-${role.valid_from || 'date'}-${index}`;

function RoleSetSnapshot({ title, roles, partyNames }) {
  return <section className="registry-change-snapshot">
    <h4>{title} <span>{roles.length}</span></h4>
    {roles.length ? <div className="registry-change-table-wrap"><table>
      <thead><tr><th>Party</th><th>Role</th><th>Share</th><th>Flags</th><th>Effective period</th><th>Evidence</th></tr></thead>
      <tbody>{roles.map((role, index) => <tr key={roleSetKey(role, index)}>
        <td><strong>{partyNames[role.party_uid] || role.party_uid}</strong>{partyNames[role.party_uid] && <small>{role.party_uid}</small>}</td>
        <td>{ROLE_LABELS[role.role_type] || role.role_type}</td>
        <td>{role.ownership_share === null || role.ownership_share === undefined ? '—' : `${role.ownership_share}%`}</td>
        <td>{[role.is_primary && 'Primary', role.is_liable_party && 'Tax liable'].filter(Boolean).join(' · ') || '—'}</td>
        <td>{role.valid_from || '—'} → {role.valid_to || 'Open'}</td>
        <td>{role.evidence?.attestation ? 'Verified' : 'Not attested'}{role.evidence?.note ? <small>{role.evidence.note}</small> : null}</td>
      </tr>)}</tbody>
    </table></div> : <p className="registry-empty">No roles in this snapshot.</p>}
  </section>;
}

export default function AdminBusinessForm() {
  const { tenantSlug, businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editing = Boolean(businessId);
  const canUpdate = hasPermission(user, 'businesses', editing ? 'update' : 'create');
  const canVerify = hasPermission(user, 'businesses', 'update');
  const canArchive = hasPermission(user, 'businesses', 'update');
  const canManageUsers = hasPermission(user, 'businesses', 'update');
  const canViewTaxDeclarations = hasPermission(user, 'tax_configuration', 'view');
  const canManageTaxDeclarations = hasPermission(user, 'tax_configuration', 'manage');
  const canApproveTaxDeclarations = hasPermission(user, 'tax_configuration', 'approve');

  const [business, setBusiness] = useState(null);
  const [form, setForm] = useState(() => {
    if (businessId) return emptyBusiness();
    const query = new URLSearchParams(window.location.search);
    return {
      ...emptyBusiness(),
      business_name: query.get('business_name') || '',
      owner_name: query.get('owner_name') || '',
      phone: query.get('phone') || '',
      email: query.get('email') || '',
      business_type: query.get('business_type') || 'other',
      address: query.get('address') || '',
      registration_number: query.get('registration_number') || '',
      commenced_on: query.get('commenced_on') || '',
    };
  });
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [grants, setGrants] = useState([]);
  const [roleDraft, setRoleDraft] = useState(emptyRole());
  const [roleParty, setRoleParty] = useState(null);
  const [locationDraft, setLocationDraft] = useState(emptyLocation());
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [businessImageUploading, setBusinessImageUploading] = useState(false);
  const [ownerPhotoUploading, setOwnerPhotoUploading] = useState(false);
  const businessImageFileInputRef = useRef(null);
  const ownerPhotoFileInputRef = useRef(null);
  const [invalidFields, setInvalidFields] = useState([]);
  const [reviewReason, setReviewReason] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [grantDraft, setGrantDraft] = useState({ user_id: '', party_uid: '', scopes: ['view', 'pay', 'view_receipts', 'download_receipts', 'submit_tax_declaration'], valid_to: '', reason: '' });
  const [roleChangeMode, setRoleChangeMode] = useState(false);
  const [roleChangeOriginal, setRoleChangeOriginal] = useState([]);
  const [roleChangeReason, setRoleChangeReason] = useState('');
  const [roleChangeRequests, setRoleChangeRequests] = useState([]);
  const [requestReviewReasons, setRequestReviewReasons] = useState({});
  const [roleChangeState, setRoleChangeState] = useState({ loading: false, error: '' });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSinglePageMode, setIsSinglePageMode] = useState(false);
  const [taxAmount, setTaxAmount] = useState('0');
  const [autoVerify, setAutoVerify] = useState(true);
  const [isCustomTax, setIsCustomTax] = useState(false);
  const [showAddGrant, setShowAddGrant] = useState(false);
  const [locationMilkatCheck, setLocationMilkatCheck] = useState({
    checking: false,
    duplicateWarning: '',
    matchedProperty: null,
  });

  // Debounced duplicate / property check for location milkat number
  useEffect(() => {
    const rawMilkat = (locationDraft.milkat_number || '').trim();
    if (!rawMilkat || rawMilkat.length < 1) {
      setLocationMilkatCheck({ checking: false, duplicateWarning: '', matchedProperty: null });
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setLocationMilkatCheck(prev => ({ ...prev, checking: true }));
        const res = await propertyAPI.checkMilkat(tenantSlug, {
          milkat_number: rawMilkat,
          exclude_business_uid: business?.business_uid,
        });
        const data = res.data?.data || res.data || {};
        const isBizDup = data.business_duplicate;
        let dupMsg = '';
        if (isBizDup && data.business) {
          dupMsg = `या मिळकत क्रमांकावर आधीच '${data.business.business_name}' हा व्यवसाय नोंदणीकृत आहे. (वेगळा गाळा असल्यास खाली शाखा / गाळा नाव टाका).`;
        }
        setLocationMilkatCheck({
          checking: false,
          duplicateWarning: dupMsg,
          matchedProperty: data.property || null,
        });
        if (data.property && !selectedProperty) {
          setSelectedProperty(data.property);
          setLocationDraft(current => ({
            ...current,
            property_uid: data.property.property_uid,
            address_line_1: current.address_line_1 || data.property.address_line_1 || '',
            locality: current.locality || data.property.locality || '',
            ward_number: current.ward_number || data.property.ward_number || '',
            latitude: current.latitude || data.property.latitude || '',
            longitude: current.longitude || data.property.longitude || '',
          }));
        }
      } catch (err) {
        setLocationMilkatCheck({ checking: false, duplicateWarning: '', matchedProperty: null });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [locationDraft.milkat_number, tenantSlug, business?.business_uid, selectedProperty]);

  // Reset to step 1 whenever businessId changes
  useEffect(() => {
    setCurrentStep(1);
  }, [businessId]);

  // Auto-clear toast popup message
  useEffect(() => {
    if (!message.text) return;
    const duration = message.type === 'success' ? 5000 : 7000;
    const timer = setTimeout(() => setMessage({ type: '', text: '' }), duration);
    return () => clearTimeout(timer);
  }, [message]);

  // Scroll smooth to invalid field if validation fails
  useEffect(() => {
    if (!invalidFields.length) return;
    const firstKey = invalidFields[0];
    const element = document.getElementById(firstKey);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus({ preventScroll: true });
    }
  }, [invalidFields, currentStep]);

  const steps = useMemo(() => {
    const list = [
      { id: 1, title: '१. मूळ माहिती', sub: 'व्यापार नाव, वर्ग व परवाना' },
      { id: 2, title: '२. संपर्क व स्थान', sub: 'संपर्क, पत्ता व जिओ-टॅग' },
      { id: 3, title: '३. मालक व भूमिका', sub: 'मालक, भागीदार व हिस्सा %' },
      { id: 4, title: '४. सर्व ठिकाणे', sub: 'शाखा व मालमत्ता लिंक' },
    ];
    if (editing) {
      list.push({ id: 5, title: '५. कर घोषणा व प्रवेश', sub: 'उलाढाल व पोर्टल ॲक्सेस' });
      list.push({ id: 6, title: '६. पडताळणी कार्यप्रवाह', sub: 'कर-पात्रता व मंजुरी' });
    } else {
      list.push({ id: 5, title: '५. पडताळणी कार्यप्रवाह', sub: 'कर-पात्रता व मसुदा सादर' });
    }
    return list;
  }, [editing]);

  const validateStep1 = useCallback(() => {
    const missing = [];
    if (!form.business_name || !form.business_name.trim()) missing.push('business_name');
    if (!form.business_type) missing.push('business_type');
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? 'कृपया व्यवसायाचे नाव व प्रकार निवडा.' : ''
    };
  }, [form.business_name, form.business_type]);

  const validateStep2 = useCallback(() => {
    const missing = [];
    if (!form.phone || !form.phone.trim()) missing.push('phone');
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? 'कृपया संपर्क विभागातील व्यवसाय फोन नंबर टाका.' : ''
    };
  }, [form.phone]);

  const validateStep3 = useCallback(() => {
    const active = activeRecords(roles);
    const hasOwner = Boolean(form.owner_name && form.owner_name.trim()) || Boolean(roleParty);
    const isValid = active.length > 0 || hasOwner;
    return {
      isValid,
      missingFields: isValid ? [] : ['roles', 'owner_name'],
      errorText: isValid ? '' : 'कृपया मालक निवडा किंवा मालकाचे नाव भरा.'
    };
  }, [roles, form.owner_name, roleParty]);

  const validateStep4 = useCallback(() => {
    const activeLocs = activeRecords(locations);
    const hasAddress = Boolean(form.address && form.address.trim());
    const isValid = activeLocs.length > 0 || hasAddress;
    return {
      isValid,
      missingFields: isValid ? [] : ['locations', 'address'],
      errorText: isValid ? '' : 'कृपया ठिकाण जोडा किंवा मुख्य पत्ता भरा.'
    };
  }, [locations, form.address]);

  const checkStepValidity = useCallback((stepNo) => {
    if (stepNo === 1) return validateStep1();
    if (stepNo === 2) return validateStep2();
    if (stepNo === 3) return validateStep3();
    if (stepNo === 4) return validateStep4();
    return { isValid: true, missingFields: [], errorText: '' };
  }, [validateStep1, validateStep2, validateStep3, validateStep4]);

  const handleStepClick = (targetStep) => {
    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      return;
    }
    for (let s = 1; s < targetStep; s++) {
      const check = checkStepValidity(s);
      if (!check.isValid) {
        setInvalidFields(check.missingFields);
        setMessage({ type: 'error', text: `पायरी ${s} अपूर्ण आहे: ${check.errorText}` });
        setCurrentStep(s);
        return;
      }
    }
    setInvalidFields([]);
    setMessage({ type: '', text: '' });
    setCurrentStep(targetStep);
  };

  const handleNextStep = () => {
    const check = checkStepValidity(currentStep);
    if (!check.isValid) {
      setInvalidFields(check.missingFields);
      return setMessage({ type: 'error', text: check.errorText });
    }
    setInvalidFields([]);
    setMessage({ type: '', text: '' });
    setCurrentStep(prev => Math.min(steps.length, prev + 1));
  };

  const applyBusiness = useCallback(data => {
    setBusiness(data);
    setForm(Object.fromEntries(CORE_KEYS.map(key => [key, data[key] ?? ''])));
    setRoles((data.party_roles || []).map(roleForEdit));
    setLocations((data.locations || []).map(locationForEdit));
    setGrants(data.access_grants || []);
  }, []);

  const loadBusiness = useCallback(async () => {
    if (!editing) return;
    setLoading(true); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.getAdminById(tenantSlug, businessId);
      applyBusiness(response.data);
    } catch (requestError) {
      setMessage({ type: 'error', text: errorMessage(requestError, 'व्यवसाय लोड करता आला नाही.') });
    } finally { setLoading(false); }
  }, [applyBusiness, businessId, editing, tenantSlug]);

  const loadRoleChangeRequests = useCallback(async () => {
    if (!editing) return;
    setRoleChangeState({ loading: true, error: '' });
    try {
      const response = await businessAPI.getPartyRoleChangeRequests(tenantSlug, businessId, { limit: 50 });
      setRoleChangeRequests(response.data.change_requests || []);
      setRoleChangeState({ loading: false, error: '' });
    } catch (requestError) {
      setRoleChangeState({ loading: false, error: errorMessage(requestError, 'Role change requests could not be loaded.') });
    }
  }, [businessId, editing, tenantSlug]);

  useEffect(() => { loadBusiness(); }, [loadBusiness]);
  useEffect(() => { loadRoleChangeRequests(); }, [loadRoleChangeRequests]);

  const readiness = useMemo(() => clientReadiness(form, roles, locations), [form, roles, locations]);
  const isArchived = business?.verification_status === 'archived';
  const isVerifiedBusiness = business?.verification_status === 'verified';
  const pendingRoleChange = roleChangeRequests.find(request => request.status === 'pending');
  const partyNames = useMemo(() => Object.fromEntries(
    [...(business?.party_roles || []), ...roles].map(role => {
      const uid = role.party_uid || role.party?.party_uid;
      return [uid, role.party?.display_name || role.party?.legal_name || uid];
    }).filter(([uid]) => uid)
  ), [business?.party_roles, roles]);

  const changeCore = event => {
    const { name, value } = event.target;
    setInvalidFields(current => current.filter(item => item !== name));
    setForm(current => ({
      ...current,
      [name]: value,
      ...(name === 'registration_type' && isUnregisteredBusiness(value) ? { registration_number: '' } : {}),
    }));
  };

  const validateImageFile = (file) => {
    if (!file) return 'कृपया प्रतिमा निवडा.';
    if (!file.type.startsWith('image/')) return 'केवळ प्रतिमा फायली स्वीकारल्या जातात.';
    if (file.size > 5 * 1024 * 1024) return 'प्रतिमा 5MB पेक्षा लहान असावी.';
    return null;
  };

  const uploadImageFile = async (file) => {
    const validationError = validateImageFile(file);
    if (validationError) throw new Error(validationError);
    const formData = new FormData();
    formData.append('image', file);
    const response = await contentAPI.uploadImage(tenantSlug, formData);
    return response.data?.url;
  };

  const handleBusinessImageFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setBusinessImageUploading(true);
    try {
      const url = await uploadImageFile(file);
      setForm(prev => ({ ...prev, business_image_url: url || prev.business_image_url }));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Business image upload failed.' });
    } finally {
      setBusinessImageUploading(false);
    }
  };

  const handleOwnerPhotoFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setOwnerPhotoUploading(true);
    try {
      const url = await uploadImageFile(file);
      setForm(prev => ({ ...prev, owner_photo_url: url || prev.owner_photo_url }));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Owner photo upload failed.' });
    } finally {
      setOwnerPhotoUploading(false);
    }
  };
  const updateRoleDraft = (key, value) => setRoleDraft(current => {
    const next = { ...current, [key]: value };
    if (key === 'role_type' && !equityRole(value)) next.ownership_share = '';
    if (key === 'role_type' && value === 'proprietor' && current.ownership_share === '') next.ownership_share = '100';
    return next;
  });
  const updateLocationDraft = (key, value) => setLocationDraft(current => ({ ...current, [key]: value }));

  const validateCore = () => {
    const errors = [];
    if (form.business_name.trim().length < 2) errors.push('व्यवसायाचे नाव किमान 2 अक्षरे असावे.');
    if (!form.business_type) errors.push('व्यवसाय प्रकार आवश्यक आहे.');
    const phone = form.phone.replace(/[\s().-]/g, '');
    if (!/^\+?\d{7,15}$/.test(phone)) errors.push('व्यवसाय फोनमध्ये 7 ते 15 अंक आवश्यक आहेत.');
    if (!form.commenced_on) errors.push('व्यवसाय सुरू झाल्याची अचूक तारीख आवश्यक आहे.');
    if (!form.owner_name.trim() && !activeRecords(roles).some(role => role.is_primary)) errors.push('मालकाचे नाव किंवा प्राथमिक पक्ष भूमिका आवश्यक आहे.');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.push('ई-मेल स्वरूप चुकीचे आहे.');
    if (form.gstin && !/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(form.gstin.toUpperCase())) errors.push('GSTIN स्वरूप चुकीचे आहे.');
    if (form.pan_last4 && !/^[A-Z0-9]{4}$/.test(form.pan_last4.toUpperCase())) errors.push('PAN ची शेवटची 4 अक्षरे/अंक द्या.');
    return errors;
  };

  const validateRole = role => {
    if (!role.party_uid && !role.party?.party_uid) return 'पक्ष निवडा.';
    if (equityRole(role.role_type)) {
      const share = Number(role.ownership_share);
      if (!Number.isFinite(share) || share <= 0 || share > 100) return 'मालकी हिस्सा 0 पेक्षा जास्त आणि 100 पर्यंत असावा.';
    }
    if (role.valid_to && role.valid_from && role.valid_to < role.valid_from) return 'समाप्ती तारीख प्रारंभापूर्वी असू शकत नाही.';
    return '';
  };

  const validateLocation = location => {
    const milkat = String(location.milkat_number || selectedProperty?.assessment_number || '').trim();
    if (!milkat) return 'मिळकत क्रमांक (Milkat Number) आवश्यक आहे.';
    if (String(location.address_line_1 || '').trim().length < 2) return 'पत्त्याची पहिली ओळ आवश्यक आहे.';
    if (location.pincode && !/^\d{6}$/.test(location.pincode)) return 'पिनकोड 6 अंकी असावा.';
    if (location.valid_to && location.valid_from && location.valid_to < location.valid_from) return 'समाप्ती तारीख प्रारंभापूर्वी असू शकत नाही.';
    if (!(Number(location.floor_area_sqft) > 0)) return 'कर गणनेसाठी ठिकाणाचे मजला क्षेत्र 0 पेक्षा जास्त असावे.';
    if (location.signage_area_sqft === '' || location.signage_area_sqft === null || location.signage_area_sqft === undefined
      || !Number.isFinite(Number(location.signage_area_sqft)) || Number(location.signage_area_sqft) < 0) {
      return 'फलक क्षेत्र आवश्यक आहे; फलक नसल्यास 0 भरा.';
    }
    return '';
  };

  const saveCore = async (event, forceAutoVerify = null) => {
    if (event && event.preventDefault) event.preventDefault();
    if (roleChangeMode) return setMessage({ type: 'error', text: 'Submit or cancel the staged role-set change before saving other business fields.' });

    const submissionForm = {
      ...form,
      commenced_on: form.commenced_on || todayIso(),
      constitution_type: form.constitution_type || 'proprietorship',
      business_category: form.business_category?.trim() || 'commercial',
      registration_type: form.registration_type?.trim() || (form.registration_number?.trim() ? 'shop_act' : 'unregistered'),
      employees_count: form.employees_count !== '' && form.employees_count !== null ? form.employees_count : 1,
      annual_turnover: form.annual_turnover !== '' && form.annual_turnover !== null ? form.annual_turnover : '0',
    };

    if (!submissionForm.business_name || !submissionForm.business_name.trim()) {
      setMessage({ type: 'error', text: 'कृपया व्यवसायाचे नाव भरा.' });
      setCurrentStep(1);
      return;
    }
    if (!submissionForm.phone || !submissionForm.phone.trim()) {
      setMessage({ type: 'error', text: 'कृपया व्यवसाय फोन नंबर भरा.' });
      setCurrentStep(2);
      return;
    }

    let submissionLocations = activeRecords(locations);
    if (submissionLocations.length === 0 && submissionForm.address) {
      submissionLocations = [{
        ...emptyLocation(),
        address_line_1: submissionForm.address,
        locality: 'गावठाण',
        ward_number: '१',
        floor_area_sqft: '100',
        signage_area_sqft: '0',
        latitude: submissionForm.latitude || '',
        longitude: submissionForm.longitude || '',
        is_primary: true,
      }];
    } else {
      submissionLocations = submissionLocations.map(loc => ({
        ...loc,
        floor_area_sqft: loc.floor_area_sqft || '100',
        signage_area_sqft: loc.signage_area_sqft || '0',
      }));
    }

    let submissionRoles = activeRecords(roles);
    if (submissionRoles.length === 0 && (roleParty || submissionForm.owner_name)) {
      submissionRoles = [{
        ...emptyRole(),
        party_uid: roleParty?.party_uid || null,
        party: roleParty || null,
        role_type: 'proprietor',
        ownership_share: '100',
        is_primary: true,
        is_liable_party: true,
      }];
    }

    setSaving('core'); setMessage({ type: '', text: '' });
    const shouldVerify = forceAutoVerify !== null ? forceAutoVerify : autoVerify;
    try {
      let response;
      if (editing) {
        response = await businessAPI.update(tenantSlug, business.business_uid, {
          version: business.version,
          ...cleanBusinessPayload(submissionForm),
        });
        applyBusiness(response.data);
        setMessage({ type: 'success', text: 'व्यवसाय माहिती अद्ययावत झाली.' });
      } else {
        response = await businessAPI.create(tenantSlug, {
          ...cleanBusinessPayload(submissionForm),
          party_roles: submissionRoles.map(cleanRolePayload),
          locations: submissionLocations.map(cleanLocationPayload),
          auto_verify: shouldVerify,
          tax_amount: Number.isFinite(Number(taxAmount)) ? Number(taxAmount) : 0,
        });
        applyBusiness(response.data);
        setMessage({
          type: 'success',
          text: shouldVerify
            ? '✓ व्यवसाय यशस्वीरित्या तयार व सक्रिय करण्यात आला!'
            : '✓ व्यवसाय यशस्वीरित्या नोंदवला गेला व तपासणीसाठी पाठवला आहे (Pending Approval).'
        });
        navigate(`/admin/${tenantSlug}/businesses`, { replace: true });
      }
    } catch (requestError) {
      setMessage({ type: 'error', text: errorMessage(requestError, 'व्यवसाय जतन करता आला नाही.') });
    } finally { setSaving(''); }
  };

  const validateRoleChangeSet = stagedRoles => {
    const errors = [];
    const currentDate = new Date().toISOString().slice(0, 10);
    if (!stagedRoles.length) errors.push('The complete role set must contain at least one role.');
    if (stagedRoles.length > 50) errors.push('A role set cannot contain more than 50 roles.');
    stagedRoles.forEach(role => {
      const issue = validateRole(role);
      if (issue) errors.push(issue);
      if ((role.valid_from || currentDate) > currentDate || (role.valid_to && role.valid_to < currentDate)) {
        errors.push('Every proposed role must be effective today; future or expired roles need a separate scheduled change.');
      }
    });
    const primary = stagedRoles.filter(role => role.is_primary);
    if (primary.length !== 1 || !primary[0]?.is_liable_party) {
      errors.push('The complete role set needs exactly one primary role and it must be tax-liable.');
    }
    const duplicateKeys = new Set();
    stagedRoles.forEach(role => {
      const key = `${role.party_uid || role.party?.party_uid}:${role.role_type}`;
      if (duplicateKeys.has(key)) errors.push('The same Party cannot have the same active role twice.');
      duplicateKeys.add(key);
    });
    const stagedReadiness = clientReadiness(form, stagedRoles, locations);
    if (stagedReadiness.missing.includes('ownership_and_role_validation')) {
      errors.push(READINESS_LABELS.ownership_and_role_validation);
    }
    return [...new Set(errors)];
  };

  const beginRoleChange = () => {
    if (!isVerifiedBusiness || !canUpdate || isArchived) return;
    if (pendingRoleChange) {
      setMessage({ type: 'error', text: `Pending request ${pendingRoleChange.request_uid} must be reviewed before another role change can be proposed.` });
      return;
    }
    const canonical = (business.party_roles || []).map(roleForEdit);
    setRoleChangeOriginal(canonical);
    setRoles(activeRecords(canonical).map(role => ({ ...role, _key: role._key || localKey('staged-role') })));
    setRoleChangeReason('');
    setRoleDraft(emptyRole());
    setRoleParty(null);
    setRoleChangeMode(true);
    setMessage({ type: '', text: '' });
  };

  const cancelRoleChange = () => {
    setRoles(roleChangeOriginal);
    setRoleChangeOriginal([]);
    setRoleChangeReason('');
    setRoleDraft(emptyRole());
    setRoleParty(null);
    setRoleChangeMode(false);
    setMessage({ type: '', text: '' });
  };

  const submitRoleChange = async () => {
    const stagedRoles = activeRecords(roles);
    const errors = validateRoleChangeSet(stagedRoles);
    if (roleChangeReason.trim().length < 3) errors.push('A proposal reason of at least 3 characters is required.');
    if (errors.length) return setMessage({ type: 'error', text: [...new Set(errors)].join(' ') });
    setSaving('role-change-submit'); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.proposePartyRoleChange(tenantSlug, business.business_uid, {
        version: business.version,
        reason: roleChangeReason.trim(),
        roles: stagedRoles.map(role => cleanRolePayload({
          ...role,
          party_uid: role.party_uid || role.party?.party_uid,
        })),
      });
      setRoleChangeRequests(current => [response.data.change_request, ...current.filter(request => request.request_uid !== response.data.change_request.request_uid)]);
      setRoles(roleChangeOriginal);
      setRoleChangeOriginal([]);
      setRoleChangeReason('');
      setRoleChangeMode(false);
      setMessage({ type: 'success', text: `Role change request ${response.data.change_request.request_uid} was submitted for independent review.` });
      await loadRoleChangeRequests();
    } catch (requestError) {
      setMessage({ type: 'error', text: roleChangeErrorMessage(requestError, 'Role change request could not be submitted.') });
      if (requestError?.response?.status === 409) await loadRoleChangeRequests();
    } finally { setSaving(''); }
  };

  const reviewRoleChange = async (request, decision) => {
    const isOwnRequest = String(request.proposed_by_user_id) === String(user?.id);
    const isStale = Number(request.base_resource_version) !== Number(business.version);
    const reason = String(requestReviewReasons[request.request_uid] || '').trim();
    if (isOwnRequest) return setMessage({ type: 'error', text: 'Maker/checker control: you cannot review your own role change request.' });
    if (decision === 'approve' && isStale) return setMessage({ type: 'error', text: 'This proposal is stale. Reject it and submit a fresh complete role set from the latest business version.' });
    if (reason.length < 3) return setMessage({ type: 'error', text: 'A review reason of at least 3 characters is required.' });
    setSaving(`role-change-${decision}-${request.request_uid}`); setMessage({ type: '', text: '' });
    try {
      const review = decision === 'approve' ? businessAPI.approvePartyRoleChange : businessAPI.rejectPartyRoleChange;
      await review(tenantSlug, business.business_uid, request.request_uid, { version: request.version, reason });
      if (decision === 'approve') await loadBusiness();
      await loadRoleChangeRequests();
      setRequestReviewReasons(current => ({ ...current, [request.request_uid]: '' }));
      setRoleChangeMode(false);
      setRoleChangeOriginal([]);
      setMessage({ type: 'success', text: decision === 'approve' ? 'The independently reviewed role set was approved and applied.' : 'The role change request was rejected; the registry was not changed.' });
    } catch (requestError) {
      setMessage({ type: 'error', text: roleChangeErrorMessage(requestError, 'Role change request could not be reviewed.') });
      if ([403, 409].includes(requestError?.response?.status)) await loadRoleChangeRequests();
    } finally { setSaving(''); }
  };

  const addRole = async () => {
    const draft = { ...roleDraft, party_uid: roleParty?.party_uid || roleDraft.party_uid, party: roleParty };
    const issue = validateRole(draft);
    if (issue) return setMessage({ type: 'error', text: issue });
    if (activeRecords(roles).some(role => (role.party_uid || role.party?.party_uid) === draft.party_uid && role.role_type === draft.role_type)) return setMessage({ type: 'error', text: 'या पक्षाची ही सक्रिय भूमिका आधीच आहे.' });
    if (draft.is_primary && activeRecords(roles).some(role => role.is_primary)) return setMessage({ type: 'error', text: 'प्राथमिक भूमिका आधीच अस्तित्वात आहे.' });
    if (!editing || roleChangeMode) {
      setRoles(current => [...current, { ...draft, _key: localKey('role'), status: 'active' }]);
      if (!editing && !form.owner_name && draft.is_primary) setForm(current => ({ ...current, owner_name: roleParty?.legal_name || roleParty?.display_name || '' }));
      setRoleDraft(emptyRole()); setRoleParty(null); setMessage({ type: '', text: '' });
      return;
    }
    if (isVerifiedBusiness) return setMessage({ type: 'error', text: 'Verified business roles can only be changed through a complete maker/checker proposal.' });
    setSaving('role-add'); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.addPartyRole(tenantSlug, business.business_uid, { version: business.version, ...cleanRolePayload(draft) });
      applyBusiness(response.data); setRoleDraft(emptyRole()); setRoleParty(null);
      setMessage({ type: 'success', text: 'पक्ष भूमिका जोडली.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'भूमिका जोडता आली नाही.') }); }
    finally { setSaving(''); }
  };

  const saveExistingRole = async role => {
    if (isVerifiedBusiness) return setMessage({ type: 'error', text: 'Verified business roles cannot be saved directly. Start an atomic role-set change.' });
    const issue = validateRole(role);
    if (issue) return setMessage({ type: 'error', text: issue });
    setSaving(`role-${role.id}`); setMessage({ type: '', text: '' });
    try {
      const payload = cleanRolePayload(role); delete payload.party_uid;
      const response = await businessAPI.updatePartyRole(tenantSlug, business.business_uid, role.id, { version: business.version, ...payload });
      applyBusiness(response.data); setMessage({ type: 'success', text: 'भूमिका अद्ययावत झाली.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'भूमिका अद्ययावत करता आली नाही.') }); }
    finally { setSaving(''); }
  };

  const endRole = async role => {
    if (isVerifiedBusiness) return setMessage({ type: 'error', text: 'Verified business roles cannot be ended directly. Start an atomic role-set change.' });
    const reason = window.prompt('भूमिका समाप्त करण्याचे कारण:');
    if (reason === null) return;
    setSaving(`role-${role.id}`); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.endPartyRole(tenantSlug, business.business_uid, role.id, { version: business.version, reason });
      applyBusiness(response.data); setMessage({ type: 'success', text: 'भूमिका इतिहास जतन करून समाप्त केली.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'भूमिका समाप्त करता आली नाही.') }); }
    finally { setSaving(''); }
  };

  const addLocation = async () => {
    if (roleChangeMode) return setMessage({ type: 'error', text: 'Submit or cancel the staged role-set change before changing locations.' });
    const milkat = String(locationDraft.milkat_number || selectedProperty?.assessment_number || '').trim();
    if (!milkat) {
      return setMessage({ type: 'error', text: 'कृपया मिळकत क्रमांक (Milkat Number) प्रविष्ट करा.' });
    }
    const draft = {
      ...locationDraft,
      milkat_number: milkat,
      branch_name: locationDraft.branch_name?.trim() || `मिळकत क्र. ${milkat}`,
      property_uid: selectedProperty?.property_uid || locationDraft.property_uid,
    };
    const issue = validateLocation(draft);
    if (issue) return setMessage({ type: 'error', text: issue });
    if (draft.is_primary && activeRecords(locations).some(location => location.is_primary)) return setMessage({ type: 'error', text: 'प्राथमिक ठिकाण आधीच अस्तित्वात आहे.' });
    if (!editing) {
      setLocations(current => [...current, { ...draft, _key: localKey('location'), status: 'active', selected_property: selectedProperty }]);
      if (!form.address && draft.is_primary) setForm(current => ({ ...current, address: [draft.address_line_1, draft.address_line_2, draft.locality].filter(Boolean).join(', ') }));
      setLocationDraft(emptyLocation()); setSelectedProperty(null); setMessage({ type: '', text: '' });
      return;
    }
    setSaving('location-add'); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.addLocation(tenantSlug, business.business_uid, { version: business.version, ...cleanLocationPayload(draft) });
      applyBusiness(response.data); setLocationDraft(emptyLocation()); setSelectedProperty(null);
      setMessage({ type: 'success', text: 'व्यवसाय ठिकाण जोडले.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'ठिकाण जोडता आले नाही.') }); }
    finally { setSaving(''); }
  };

  const updateLocationRow = (id, key, value) => setLocations(current => current.map(location => ((location.id || location._key) === id ? { ...location, [key]: value } : location)));
  const saveExistingLocation = async location => {
    if (roleChangeMode) return setMessage({ type: 'error', text: 'Submit or cancel the staged role-set change before changing locations.' });
    const issue = validateLocation(location);
    if (issue) return setMessage({ type: 'error', text: issue });
    const payload = cleanLocationPayload(location);
    if (location.property_action === 'keep') delete payload.property_uid;
    if (location.property_action === 'remove') payload.property_uid = null;
    setSaving(`location-${location.id}`); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.updateLocation(tenantSlug, business.business_uid, location.id, { version: business.version, ...payload });
      applyBusiness(response.data); setMessage({ type: 'success', text: 'ठिकाण अद्ययावत झाले.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'ठिकाण अद्ययावत करता आले नाही.') }); }
    finally { setSaving(''); }
  };

  const closeLocation = async location => {
    if (roleChangeMode) return setMessage({ type: 'error', text: 'Submit or cancel the staged role-set change before changing locations.' });
    const reason = window.prompt('ठिकाण बंद करण्याचे कारण:');
    if (reason === null) return;
    setSaving(`location-${location.id}`); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.closeLocation(tenantSlug, business.business_uid, location.id, { version: business.version, reason });
      applyBusiness(response.data); setMessage({ type: 'success', text: 'ठिकाण इतिहास जतन करून बंद केले.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'ठिकाण बंद करता आले नाही.') }); }
    finally { setSaving(''); }
  };

  const transition = async action => {
    if (roleChangeMode) return setMessage({ type: 'error', text: 'Submit or cancel the staged role-set change before changing business status.' });
    const effectiveReason = reviewReason.trim() || (action === 'verify' ? 'प्रशासकीय पडताळणी व मंजुरी' : '');
    if (['reject', 'archive'].includes(action) && effectiveReason.length < 3) return setMessage({ type: 'error', text: 'कारण किमान 3 अक्षरांचे आवश्यक आहे.' });
    setSaving(action); setMessage({ type: '', text: '' });
    try {
      let currentVersion = business.version;
      if (action === 'verify') {
        const updateResp = await businessAPI.update(tenantSlug, business.business_uid, {
          version: currentVersion,
          ...cleanBusinessPayload(form),
        });
        currentVersion = updateResp.data.version;
      }
      const response = await businessAPI[action](tenantSlug, business.business_uid, { version: currentVersion, reason: effectiveReason || undefined });
      applyBusiness(response.data); setReviewReason('');
      setMessage({ type: 'success', text: `स्थिती “${STATUS_LABELS[response.data.verification_status] || response.data.verification_status}” झाली.` });
    } catch (requestError) {
      setMessage({ type: 'error', text: errorMessage(requestError, 'स्थिती बदलता आली नाही.') });
    } finally { setSaving(''); }
  };

    const searchUsers = async () => {
      setSaving('user-search'); setMessage({ type: '', text: '' });
      try {
        const response = await userApprovalAPI.getAll(tenantSlug, { status: 'approved', search: userQuery.trim() || undefined, limit: 20 });
        setUserResults(response.data.users || []);
      } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'वापरकर्ते शोधता आले नाहीत.') }); }
      finally { setSaving(''); }
    };

    const createGrant = async () => {
      if (!grantDraft.user_id || !grantDraft.scopes.length) return setMessage({ type: 'error', text: 'वापरकर्ता आणि किमान एक परवानगी निवडा.' });
      setSaving('grant'); setMessage({ type: '', text: '' });
      try {
        const response = await businessAPI.createAccessGrant(tenantSlug, business.business_uid, {
          user_id: Number(grantDraft.user_id), party_uid: grantDraft.party_uid || null,
          scopes: grantDraft.scopes, valid_to: grantDraft.valid_to || null,
          reason: grantDraft.reason.trim() || null,
        });
        setGrants(current => [response.data, ...current]);
        setGrantDraft({ user_id: '', party_uid: '', scopes: ['view', 'pay', 'view_receipts', 'download_receipts', 'submit_tax_declaration'], valid_to: '', reason: '' });
        setMessage({ type: 'success', text: 'पोर्टल प्रवेश मंजूर केला.' });
      } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'प्रवेश मंजूर करता आला नाही.') }); }
      finally { setSaving(''); }
    };

    const revokeGrant = async grant => {
      const reason = window.prompt('प्रवेश रद्द करण्याचे कारण:');
      if (reason === null) return;
      setSaving(`grant-${grant.grant_uid}`); setMessage({ type: '', text: '' });
      try {
        await businessAPI.revokeAccessGrant(tenantSlug, business.business_uid, grant.grant_uid, { reason });
        setGrants(current => current.map(item => item.grant_uid === grant.grant_uid ? { ...item, status: 'revoked' } : item));
        setMessage({ type: 'success', text: 'प्रवेश रद्द केला.' });
      } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'प्रवेश रद्द करता आला नाही.') }); }
      finally { setSaving(''); }
    };

    if (loading) return <div className="business-registry-form"><div className="registry-loading">व्यवसाय नोंद लोड होत आहे…</div></div>;

    return (
      <div className="business-registry-form">
        <header className="registry-form-header">
          <div><button type="button" className="registry-back" onClick={() => navigate(`/admin/${tenantSlug}/businesses`)}>← व्यवसाय सूची</button><h1>{editing ? 'व्यवसाय व्यवस्थापन' : 'नवीन व्यवसाय नोंदणी'}</h1><p>{editing ? `${business?.business_uid} · आवृत्ती v${business?.version}` : 'व्यवसाय, पक्ष भूमिका आणि ठिकाणे एका सुरक्षित व्यवहारात जतन होतील.'}</p></div>
          {editing && <div className="registry-header-badges"><span className={`business-verify ${business.verification_status}`}>{STATUS_LABELS[business.verification_status]}</span><span className={`business-readiness ${readiness.ready ? 'ready' : 'review'}`}>{readiness.ready ? '✓ पडताळणीसाठी तयार' : '⚠ तपासणी आवश्यक'}</span></div>}
        </header>

        {/* Global Floating Toast Popup */}
        {message.text && (
          <div className="gp-toast-container">
            <div className={`gp-toast gp-toast-${message.type || 'info'}`} role="alert">
              <span className="gp-toast-icon">
                {message.type === 'success' ? '✓' : message.type === 'error' ? '⚠' : 'ℹ'}
              </span>
              <div className="gp-toast-content">
                <div className="gp-toast-title">
                  {message.type === 'success' ? 'यशस्वी नोंदणी / अपडेट' : message.type === 'error' ? 'फॉर्म अपूर्ण / त्रुटी' : 'माहिती'}
                </div>
                <div className="gp-toast-message">
                  {message.text}
                  {message.type === 'error' && editing && /changed|refresh|stale|pending|conflict|version|आवृत्ती/i.test(message.text) && (
                    <div style={{ marginTop: '6px' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => { setRoleChangeMode(false); setRoleChangeOriginal([]); loadBusiness(); loadRoleChangeRequests(); }}
                      >
                        पुन्हा लोड करा
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="gp-toast-close"
                onClick={() => setMessage({ type: '', text: '' })}
                title="बंद करा"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {!isSinglePageMode && (
          <div className="registry-stepper-container">
            <div className="registry-stepper-header">
              <span>पायरी {currentStep} / {steps.length}: <strong>{steps[currentStep - 1]?.title}</strong></span>
              <button
                type="button"
                className="registry-stepper-toggle"
                onClick={() => setIsSinglePageMode(true)}
              >
                📄 सर्व विभाग एकत्र पहा (Full Page)
              </button>
            </div>
            <div className="registry-stepper">
              {steps.map((step, idx) => {
                const stepNum = idx + 1;
                const isActive = currentStep === stepNum;
                const isCompleted = currentStep > stepNum;
                const isLocked = stepNum > currentStep && !checkStepValidity(stepNum - 1).isValid;
                return (
                  <button
                    key={step.id}
                    type="button"
                    className={`stepper-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isLocked ? 'restricted' : ''}`}
                    onClick={() => handleStepClick(stepNum)}
                    title={isLocked ? `पायरी ${stepNum - 1} अपूर्ण आहे` : step.title}
                  >
                    <span className="step-num">{isCompleted ? '✓' : isLocked ? '🔒' : stepNum}</span>
                    <div className="step-label">
                      <strong>{step.title}</strong>
                      <small>{step.sub}</small>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isSinglePageMode && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
            <button
              type="button"
              className="registry-stepper-toggle"
              onClick={() => setIsSinglePageMode(false)}
            >
              🧩 बहु-पायरी फॉर्मवर जा (Multi-Step Wizard)
            </button>
          </div>
        )}

        <form onSubmit={saveCore}>
          {(isSinglePageMode || currentStep === 1) && <section className="registry-card">
            <div className="registry-section-heading"><span>1</span><div><h2>कायदेशीर आणि व्यावसायिक माहिती</h2><p>Legal identity, classification and private compliance fields</p></div></div>
            <div className="registry-grid">
              <Field
                label="प्रदर्शित / व्यापार नाव"
                name="business_name"
                value={form.business_name}
                onChange={changeCore}
                required
                isInvalid={invalidFields.includes('business_name')}
                errorHint="व्यापार नाव टाकणे आवश्यक आहे."
                maxLength="200"
              />
              <Field label="कायदेशीर नाव" name="legal_name" value={form.legal_name} onChange={changeCore} optional maxLength="240" />
              <Field label="मालक नाव (जुनी/द्रुत नोंद)" name="owner_name" value={form.owner_name} onChange={changeCore} optional help="प्राथमिक पक्ष भूमिकेवरूनही हे नाव भरले जाऊ शकते." />
              <SelectField
                label="संस्थात्मक रचना"
                name="constitution_type"
                value={form.constitution_type}
                onChange={changeCore}
                options={CONSTITUTION_TYPES.map(item => ({ value: item, label: CONSTITUTION_LABELS[item] }))}
                required
                isInvalid={invalidFields.includes('constitution_type')}
                errorHint="संस्थात्मक रचना निवडणे आवश्यक आहे."
              />
              <SelectField
                label="व्यवसाय प्रकार"
                name="business_type"
                value={form.business_type}
                onChange={changeCore}
                options={BUSINESS_TYPES.map(item => ({ value: item, label: TYPE_LABELS[item] }))}
                required
                isInvalid={invalidFields.includes('business_type')}
                errorHint="व्यवसाय प्रकार निवडणे आवश्यक आहे."
              />
              <Field label="उप-वर्ग / Category" name="business_category" value={form.business_category} onChange={changeCore} required isInvalid={invalidFields.includes('business_category')} errorHint="कर आकारणीसाठी व्यवसायाची अचूक श्रेणी आवश्यक आहे." />
              <Field label="नोंदणी प्रकार" name="registration_type" value={form.registration_type} onChange={changeCore} optional placeholder="Shop Act, Udyam, Unregistered…" help="नोंदणी प्रकार माहित नसल्यास रिक्त ठेवा." />
              <Field label="नोंदणी क्रमांक" name="registration_number" value={form.registration_number} onChange={changeCore} optional placeholder="उदा. MH-12-345" help="उपलब्ध असल्यास टाका; नसल्यास रिक्त ठेवा." />
              <Field label="GSTIN (खाजगी)" name="gstin" value={form.gstin} onChange={changeCore} optional maxLength="15" />
              <Field label="PAN शेवटची 4 (खाजगी)" name="pan_last4" value={form.pan_last4} onChange={changeCore} optional maxLength="4" />
              <Field label="कर्मचारी संख्या" name="employees_count" type="number" min="0" value={form.employees_count} onChange={changeCore} required isInvalid={invalidFields.includes('employees_count')} errorHint="कर्मचारी नसल्यास 0 भरा." disabled={business?.verification_status === 'verified'} help={business?.verification_status === 'verified' ? 'Use the FY tax declaration section below.' : undefined} />
              <Field label="वार्षिक उलाढाल / Turnover" name="annual_turnover" type="number" min="0" step="0.01" value={form.annual_turnover} onChange={changeCore} required isInvalid={invalidFields.includes('annual_turnover')} errorHint="अचूक रक्कम भरा; उलाढाल नसल्यास 0 भरा." disabled={business?.verification_status === 'verified'} placeholder="Exact numeric amount, e.g. 1200000" help={business?.verification_status === 'verified' ? 'Use the FY tax declaration section below.' : 'Legacy compatibility only; approved FY declarations take precedence.'} />
              <Field label="स्थापना वर्ष" name="establishment_year" type="number" min="1700" max={new Date().getFullYear()} value={form.establishment_year} onChange={changeCore} optional />
              <Field
                label="व्यवसाय प्रारंभ तारीख / Commenced on"
                name="commenced_on"
                type="date"
                value={form.commenced_on}
                onChange={changeCore}
                required
                isInvalid={invalidFields.includes('commenced_on')}
                errorHint="प्रारंभ तारीख निवडणे आवश्यक आहे."
                help="Tax applicability uses this exact date; the year field is descriptive only."
              />
              <Field label="उघडण्याची वेळ" name="opening_hours" value={form.opening_hours} onChange={changeCore} optional />
              <label className="registry-field wide" htmlFor="description"><span>वर्णन (ऐच्छिक)</span><textarea id="description" name="description" rows="3" value={form.description} onChange={changeCore} /></label>
              <label className="registry-field wide" htmlFor="products_services"><span>उत्पादने आणि सेवा (ऐच्छिक)</span><textarea id="products_services" name="products_services" rows="3" value={form.products_services} onChange={changeCore} /></label>
            </div>
          </section>}

          {(isSinglePageMode || currentStep === 2) && <section className="registry-card">
            <div className="registry-section-heading"><span>2</span><div><h2>संपर्क आणि सार्वजनिक निर्देशिका</h2><p>व्यवसाय फोन, ई-मेल, वेबसाइट व पत्ता पडताळणीनंतर सार्वजनिक होऊ शकतात; संपर्क व्यक्ती आणि मालक फोटो खाजगी राहतात.</p></div></div>
            <div className="registry-grid">
              <Field
                label="व्यवसाय फोन"
                name="phone"
                value={form.phone}
                onChange={changeCore}
                required
                isInvalid={invalidFields.includes('phone')}
                errorHint="व्यवसाय फोन नंबर टाकणे आवश्यक आहे."
                inputMode="tel"
              />
              <Field label="व्यवसाय ई-मेल" name="email" type="email" value={form.email} onChange={changeCore} optional />
              <Field label="वेबसाइट" name="website" value={form.website} onChange={changeCore} optional placeholder="https://…" />
              <Field label="संपर्क व्यक्ती" name="contact_person" value={form.contact_person} onChange={changeCore} optional />
              <Field label="संपर्क व्यक्तीचा फोन" name="contact_person_phone" value={form.contact_person_phone} onChange={changeCore} optional inputMode="tel" />
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr auto', alignItems: 'flex-end' }}>
                <label className="registry-field" htmlFor="business_image_url">
                  <span>व्यवसाय प्रतिमा URL</span>
                  <input
                    id="business_image_url"
                    name="business_image_url"
                    value={form.business_image_url || ''}
                    onChange={changeCore}
                    placeholder="https://example.com/business-photo.jpg"
                  />
                </label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => businessImageFileInputRef.current?.click()}
                    disabled={businessImageUploading}
                  >
                    {businessImageUploading ? 'Uploading...' : 'Upload'}
                  </button>
                  <input ref={businessImageFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBusinessImageFile} />
                </div>
              </div>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr auto', alignItems: 'flex-end' }}>
                <label className="registry-field" htmlFor="owner_photo_url">
                  <span>मालक फोटो URL (खाजगी)</span>
                  <input
                    id="owner_photo_url"
                    name="owner_photo_url"
                    value={form.owner_photo_url || ''}
                    onChange={changeCore}
                    placeholder="https://example.com/owner-photo.jpg"
                  />
                </label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => ownerPhotoFileInputRef.current?.click()}
                    disabled={ownerPhotoUploading}
                  >
                    {ownerPhotoUploading ? 'Uploading...' : 'Upload'}
                  </button>
                  <input ref={ownerPhotoFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleOwnerPhotoFile} />
                </div>
              </div>
              <LocationPicker
                id="business-main-location"
                latitude={form.latitude}
                longitude={form.longitude}
                onChange={coordinates => setForm(current => ({ ...current, ...coordinates }))}
                disabled={!canUpdate || isArchived}
                label="मुख्य व्यवसाय स्थान / Main business location"
              />
              <label className="registry-field wide" htmlFor="address"><span>मुख्य पत्ता</span><textarea id="address" name="address" rows="2" value={form.address} onChange={changeCore} /></label>
            </div>
          </section>}

          {(isSinglePageMode || currentStep === 3) && <section id="business-party-role-changes" className={`registry-card ${roleChangeMode ? 'registry-card-staging' : ''}`}>
            <div className="registry-section-heading"><span>3</span><div><h2>पक्ष, मालकी आणि कर-दायित्व</h2><p>Village resident असणे आवश्यक नाही; external taxpayer किंवा organization Party वापरा.</p></div></div>
            {isVerifiedBusiness && <div className={`registry-change-guard ${roleChangeMode ? 'editing' : ''}`}>
              <div><strong>{roleChangeMode ? 'Atomic role-set edit is active' : 'Verified registry record'}</strong><p>{roleChangeMode ? 'Edit the complete effective role set below. Nothing changes in the registry until another authorised user approves it.' : 'Roles are locked against direct add, edit, and removal. Start a maker/checker request to replace the complete set.'}</p></div>
              {!roleChangeMode ? <button type="button" onClick={beginRoleChange} disabled={!canUpdate || Boolean(saving) || isArchived || roleChangeState.loading || Boolean(pendingRoleChange)}>{pendingRoleChange ? 'Review pending request first' : 'Start atomic role change'}</button> : <button type="button" className="secondary" onClick={cancelRoleChange} disabled={Boolean(saving)}>Cancel staged changes</button>}
            </div>}

            {(!isVerifiedBusiness || roleChangeMode) && <>
              <PartyFinder tenantSlug={tenantSlug} onSelect={party => {
                setRoleParty(party);
                setRoleDraft(current => ({
                  ...current,
                  party_uid: party.party_uid || null,
                  party,
                  ownership_share: current.ownership_share || '100',
                  role_type: current.role_type || 'proprietor',
                  is_primary: true,
                  is_liable_party: true,
                }));
                if (!form.owner_name) {
                  setForm(current => ({ ...current, owner_name: party.display_name || party.legal_name || '' }));
                }
              }} />
              {roleParty && <div className="registry-selection"><strong>निवडलेले मालक / व्यक्ती: {roleParty.display_name || roleParty.legal_name}</strong><span>{roleParty.primary_phone ? `फोन: ${roleParty.primary_phone}` : ''} {roleParty.party_uid ? `· ${roleParty.party_uid}` : ''}</span></div>}
              <div className="registry-grid compact-grid">
                <SelectField label="भूमिका" name="role-type" value={roleDraft.role_type} onChange={event => updateRoleDraft('role_type', event.target.value)} options={PARTY_ROLE_TYPES.map(item => ({ value: item, label: ROLE_LABELS[item] }))} />
                <Field label="मालकी हिस्सा %" name="role-share" type="number" min="0.0001" max="100" step="0.0001" disabled={!equityRole(roleDraft.role_type)} value={roleDraft.ownership_share} onChange={event => updateRoleDraft('ownership_share', event.target.value)} />
                <Field label="प्रारंभ" name="role-from" type="date" value={roleDraft.valid_from} onChange={event => updateRoleDraft('valid_from', event.target.value)} />
                <Field label="समाप्ती (ऐच्छिक)" name="role-to" type="date" value={roleDraft.valid_to} onChange={event => updateRoleDraft('valid_to', event.target.value)} />
                <div className="registry-check-row"><Checkbox label="प्राथमिक / Primary" checked={roleDraft.is_primary} onChange={event => updateRoleDraft('is_primary', event.target.checked)} /><Checkbox label="कर-दायी / Liable" checked={roleDraft.is_liable_party} onChange={event => updateRoleDraft('is_liable_party', event.target.checked)} /><Checkbox label="पुरावा पडताळला" checked={roleDraft.evidence_attestation} onChange={event => updateRoleDraft('evidence_attestation', event.target.checked)} /></div>
                <Field label="पुरावा नोंद" name="role-evidence" value={roleDraft.evidence_note} onChange={event => updateRoleDraft('evidence_note', event.target.value)} />
              </div>
              <button type="button" className="registry-add-button" onClick={addRole} disabled={!canUpdate || Boolean(saving) || isArchived}>
                {saving === 'role-add' ? <><span className="btn-spinner"></span> जतन होत आहे…</> : `+ ${roleChangeMode ? 'Add to proposed set' : 'भूमिका जोडा'}`}
              </button>
            </>}

            <div className="registry-editor-list">
              {roles.map((role, index) => <article key={role.id || role._key} className={`${role.status !== 'active' ? 'historical' : ''} ${roleChangeMode ? 'staged' : ''}`}>
                <header><div><strong>{role.party?.display_name || role.party?.legal_name || role.party_uid}</strong><small>{role.party?.party_uid || role.party_uid}{(role.party?.profile_kind || role.party?.tenant_profile?.profile_kind) ? ` · ${role.party?.profile_kind || role.party?.tenant_profile?.profile_kind}` : ''}{(role.party?.kyc_status || role.party?.tenant_profile?.kyc_status) ? ` · KYC ${role.party?.kyc_status || role.party?.tenant_profile?.kyc_status}` : ''}</small></div><span>{roleChangeMode ? 'proposed' : (role.status || 'active')}</span></header>
                <div className="registry-grid compact-grid">
                  <SelectField label="भूमिका" name={`role-type-${index}`} value={role.role_type} disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode)} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, role_type: event.target.value, ownership_share: equityRole(event.target.value) ? item.ownership_share : '' } : item))} options={PARTY_ROLE_TYPES.map(item => ({ value: item, label: ROLE_LABELS[item] }))} />
                  <Field label="मालकी %" name={`role-share-${index}`} type="number" step="0.0001" min="0.0001" max="100" disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode) || !equityRole(role.role_type)} value={role.ownership_share} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, ownership_share: event.target.value } : item))} />
                  <Field label="प्रारंभ" name={`role-from-${index}`} type="date" disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode)} value={role.valid_from} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, valid_from: event.target.value } : item))} />
                  <Field label="समाप्ती" name={`role-to-${index}`} type="date" disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode)} value={role.valid_to} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, valid_to: event.target.value } : item))} />
                  <div className="registry-check-row"><Checkbox label="प्राथमिक" checked={role.is_primary} disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode)} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, is_primary: event.target.checked } : item))} /><Checkbox label="कर-दायी" checked={role.is_liable_party} disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode)} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, is_liable_party: event.target.checked } : item))} /><Checkbox label="पुरावा पडताळला" checked={role.evidence_attestation} disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode)} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, evidence_attestation: event.target.checked } : item))} /></div>
                  <Field label="पुरावा नोंद" name={`role-evidence-${index}`} value={role.evidence_note} disabled={role.status !== 'active' || (isVerifiedBusiness && !roleChangeMode)} onChange={event => setRoles(current => current.map((item, i) => i === index ? { ...item, evidence_note: event.target.value } : item))} />
                </div>
                {role.status === 'active' && <footer>{roleChangeMode ? <button type="button" className="danger" onClick={() => setRoles(current => current.filter((_, i) => i !== index))} disabled={Boolean(saving)}>Remove from proposed set</button> : editing ? (!isVerifiedBusiness && <><button type="button" onClick={() => saveExistingRole(role)} disabled={Boolean(saving) || !canUpdate || isArchived}>बदल जतन</button><button type="button" className="danger" onClick={() => endRole(role)} disabled={Boolean(saving) || !canUpdate || isArchived}>भूमिका समाप्त</button></>) : <button type="button" className="danger" onClick={() => setRoles(current => current.filter((_, i) => i !== index))}>काढा</button>}</footer>}
              </article>)}
              {!roles.length && <p className="registry-empty">अद्याप पक्ष भूमिका जोडलेली नाही.</p>}
            </div>
            <div className={`registry-readiness-summary ${readiness.ownershipTotal > 100 ? 'bad' : ''}`}>सक्रिय मालकी हिस्सा: <strong>{readiness.ownershipTotal.toFixed(4).replace(/\.?0+$/, '')}%</strong></div>
            {roleChangeMode && <div className="registry-change-submit">
              <label className="registry-field wide" htmlFor="role-change-reason"><span>Change reason *</span><textarea id="role-change-reason" rows="3" maxLength="2000" value={roleChangeReason} onChange={event => setRoleChangeReason(event.target.value)} placeholder="Explain the ownership, liability, or representative change for the independent reviewer." /></label>
              <div><button type="button" className="secondary" onClick={cancelRoleChange} disabled={Boolean(saving)}>Cancel</button><button type="button" onClick={submitRoleChange} disabled={Boolean(saving)}>{saving === 'role-change-submit' ? 'Submitting…' : 'Submit complete set for review'}</button></div>
            </div>}

            {editing && <div className="registry-change-history">
              <div className="registry-change-history-heading"><div><h3>Party-role change requests</h3><p>Immutable before/after snapshots and independent review history for this business.</p></div><button type="button" className="secondary" onClick={loadRoleChangeRequests} disabled={roleChangeState.loading}>{roleChangeState.loading ? 'Refreshing…' : 'Refresh requests'}</button></div>
              {roleChangeState.error && <div className="registry-inline-error" role="alert">{roleChangeState.error}</div>}
              {!roleChangeState.loading && !roleChangeRequests.length && !roleChangeState.error && <p className="registry-empty">No party-role change requests have been submitted.</p>}
              <div className="registry-change-request-list">{roleChangeRequests.map(request => {
                const ownRequest = String(request.proposed_by_user_id) === String(user?.id);
                const staleRequest = request.status === 'pending' && Number(request.base_resource_version) !== Number(business.version);
                return <article key={request.request_uid} className={`registry-change-request ${request.status}`}>
                  <header><div><strong>{request.request_uid}</strong><small>Business v{request.base_resource_version} · request v{request.version} · proposed {displayDateTime(request.proposed_at)}</small></div><span>{request.status}</span></header>
                  <dl><div><dt>Maker</dt><dd>User #{request.proposed_by_user_id}</dd></div><div><dt>Proposal reason</dt><dd>{request.proposal_reason}</dd></div>{request.reviewed_by_user_id && <div><dt>Checker</dt><dd>User #{request.reviewed_by_user_id} · {displayDateTime(request.reviewed_at)}</dd></div>}{request.review_reason && <div><dt>Review reason</dt><dd>{request.review_reason}</dd></div>}</dl>
                  {staleRequest && <div className="registry-change-warning">Registry is now v{business.version}; approval is blocked. Reject this request and create a fresh proposal.</div>}
                  <div className="registry-change-comparison"><RoleSetSnapshot title="Immutable baseline" roles={request.baseline_set || []} partyNames={partyNames} /><RoleSetSnapshot title="Requested complete set" roles={request.requested_set || []} partyNames={partyNames} /></div>
                  {request.status === 'pending' && canVerify && <div className="registry-change-review">
                    {!ownRequest && <label className="registry-field wide" htmlFor={`role-review-${request.request_uid}`}><span>Independent review reason *</span><textarea id={`role-review-${request.request_uid}`} rows="2" maxLength="2000" value={requestReviewReasons[request.request_uid] || ''} onChange={event => setRequestReviewReasons(current => ({ ...current, [request.request_uid]: event.target.value }))} /></label>}
                    {ownRequest && <p>Maker/checker control: you submitted this request, so another user with <code>businesses.verify</code> must review it.</p>}
                    <div><button type="button" className="danger" onClick={() => reviewRoleChange(request, 'reject')} disabled={Boolean(saving) || ownRequest}>Reject request</button><button type="button" className="success" onClick={() => reviewRoleChange(request, 'approve')} disabled={Boolean(saving) || ownRequest || staleRequest}>{saving === `role-change-approve-${request.request_uid}` ? 'Approving…' : 'Approve and apply'}</button></div>
                  </div>}
                </article>;
              })}</div>
            </div>}
          </section>}

          {(isSinglePageMode || currentStep === 4) && <section className="registry-card">
            <div className="registry-section-heading"><span>4</span><div><h2>व्यवसाय ठिकाणे आणि मालमत्ता लिंक</h2><p>Property Registry शी लिंक करा किंवा स्वतंत्र पत्ता नोंदवा.</p></div></div>
            <PropertyFinder
              tenantSlug={tenantSlug}
              excludeBusinessUid={business?.business_uid}
              onSelect={property => {
                setSelectedProperty(property);
                setLocationDraft(current => ({
                  ...current,
                  milkat_number: property.assessment_number || '',
                  property_uid: property.property_uid,
                  address_line_1: current.address_line_1 || property.address_line_1 || '',
                  locality: current.locality || property.locality || '',
                  ward_number: current.ward_number || property.ward_number || '',
                  latitude: current.latitude || property.latitude || '',
                  longitude: current.longitude || property.longitude || '',
                }));
              }}
            />
            {selectedProperty && <div className="registry-selection"><strong>लिंक: {selectedProperty.property_name} · {selectedProperty.assessment_number}</strong><span>{selectedProperty.property_uid}</span><button type="button" onClick={() => { setSelectedProperty(null); setLocationDraft(current => ({ ...current, property_uid: '' })); }}>लिंक काढा</button></div>}
            <div className="registry-grid compact-grid">
              <Field
                label="मिळकत क्रमांक"
                name="location-milkat-number"
                value={locationDraft.milkat_number || selectedProperty?.assessment_number || ''}
                onChange={event => {
                  const val = event.target.value;
                  updateLocationDraft('milkat_number', val);
                  if (selectedProperty && selectedProperty.assessment_number !== val) {
                    setSelectedProperty(null);
                    updateLocationDraft('property_uid', '');
                  }
                }}
                required
                placeholder="उदा. 123 किंवा 45/A"
                isInvalid={Boolean(locationMilkatCheck.duplicateWarning)}
                errorHint={locationMilkatCheck.duplicateWarning}
                help={locationMilkatCheck.checking
                  ? 'मिळकत तपासत आहे...'
                  : selectedProperty
                    ? `✓ मालमत्ता नोंदवहीत लिंक: ${selectedProperty.property_name} (${selectedProperty.assessment_number})`
                    : 'व्यावसायिक जागेचा ग्रामपंचायत मिळकत क्रमांक (भाड्याची / नोंदवहीत नसलेली जागा असल्यास थेट टाका)'}
              />
              <Field label="शाखा / ठिकाण नाव" name="location-branch" value={locationDraft.branch_name} onChange={event => updateLocationDraft('branch_name', event.target.value)} optional placeholder="उदा. गाळा क्र. २ / मुख्य शाखा" />
              <SelectField label="वापर हक्क" name="location-occupancy" value={locationDraft.occupancy_type} onChange={event => updateLocationDraft('occupancy_type', event.target.value)} options={OCCUPANCY_TYPES} optional />
              <Field label="पत्ता ओळ 1" name="location-address-1" value={locationDraft.address_line_1} onChange={event => updateLocationDraft('address_line_1', event.target.value)} required />
              <Field label="पत्ता ओळ 2" name="location-address-2" value={locationDraft.address_line_2} onChange={event => updateLocationDraft('address_line_2', event.target.value)} optional />
              <Field label="परिसर" name="location-locality" value={locationDraft.locality} onChange={event => updateLocationDraft('locality', event.target.value)} optional />
              <Field label="वॉर्ड" name="location-ward" value={locationDraft.ward_number} onChange={event => updateLocationDraft('ward_number', event.target.value)} optional />
              <Field label="पिनकोड" name="location-pincode" value={locationDraft.pincode} maxLength="6" inputMode="numeric" onChange={event => updateLocationDraft('pincode', event.target.value)} optional />
              <Field label="मजला क्षेत्र sq.ft" name="location-floor" type="number" min="0.01" step="0.01" value={locationDraft.floor_area_sqft} onChange={event => updateLocationDraft('floor_area_sqft', event.target.value)} required />
              <Field label="फलक क्षेत्र sq.ft" name="location-signage" type="number" min="0" step="0.01" value={locationDraft.signage_area_sqft} onChange={event => updateLocationDraft('signage_area_sqft', event.target.value)} required help="फलक नसल्यास 0 भरा; जाहिरात/फलक करासाठी हे मूल्य वापरले जाते." />
              <Field label="प्रारंभ" name="location-from" type="date" value={locationDraft.valid_from} onChange={event => updateLocationDraft('valid_from', event.target.value)} optional />
              <Field label="समाप्ती" name="location-to" type="date" value={locationDraft.valid_to} onChange={event => updateLocationDraft('valid_to', event.target.value)} optional />
              <div className="registry-check-row"><Checkbox label="प्राथमिक ठिकाण" checked={locationDraft.is_primary} onChange={event => updateLocationDraft('is_primary', event.target.checked)} /></div>
              <LocationPicker
                id="business-new-branch-location"
                latitude={locationDraft.latitude}
                longitude={locationDraft.longitude}
                onChange={coordinates => setLocationDraft(current => ({ ...current, ...coordinates }))}
                disabled={!canUpdate || isArchived}
                label="शाखेचे स्थान / Branch location"
              />
            </div>
            <button type="button" className="registry-add-button" onClick={addLocation} disabled={!canUpdate || Boolean(saving) || isArchived || roleChangeMode}>
              {saving === 'location-add' ? <><span className="btn-spinner"></span> जोडत आहे…</> : '+ ठिकाण जोडा'}
            </button>

            <div className="registry-editor-list location-list">
              {locations.map((location, index) => <article key={location.id || location._key} className={location.status !== 'active' ? 'historical' : ''}>
                <header>
                  <div>
                    <strong>{location.branch_name || (location.milkat_number ? `मिळकत क्र. ${location.milkat_number}` : `ठिकाण ${index + 1}`)}</strong>
                    <small>
                      {location.milkat_number ? `मिळकत क्र: ${location.milkat_number} · ` : ''}
                      {location.location_uid || location.selected_property?.property_uid || 'नवीन नोंद'}
                    </small>
                  </div>
                  <span>{location.status || 'active'}{location.is_primary ? ' · primary' : ''}</span>
                </header>
                <div className="registry-grid compact-grid">
                  <Field label="मिळकत क्रमांक" name={`loc-milkat-${index}`} value={location.milkat_number || ''} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'milkat_number', event.target.value)} optional />
                  <Field label="शाखा नाव" name={`loc-branch-${index}`} value={location.branch_name} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'branch_name', event.target.value)} optional />
                  <Field label="पत्ता ओळ 1" name={`loc-address-${index}`} value={location.address_line_1} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'address_line_1', event.target.value)} optional />
                  <Field label="परिसर" name={`loc-locality-${index}`} value={location.locality} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'locality', event.target.value)} optional />
                  <Field label="वॉर्ड" name={`loc-ward-${index}`} value={location.ward_number} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'ward_number', event.target.value)} optional />
                  <Field label="मजला क्षेत्र" name={`loc-floor-${index}`} type="number" min="0.01" step="0.01" value={location.floor_area_sqft} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'floor_area_sqft', event.target.value)} required />
                  <Field label="फलक क्षेत्र" name={`loc-signage-${index}`} type="number" min="0" step="0.01" value={location.signage_area_sqft} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'signage_area_sqft', event.target.value)} required help="फलक नसल्यास 0 भरा." />
                  <SelectField label="वापर हक्क" name={`loc-occupancy-${index}`} value={location.occupancy_type} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'occupancy_type', event.target.value)} options={OCCUPANCY_TYPES} optional />
                  <div className="registry-check-row"><Checkbox label="प्राथमिक" checked={location.is_primary} disabled={location.status !== 'active'} onChange={event => updateLocationRow(location.id || location._key, 'is_primary', event.target.checked)} /></div>
                  <LocationPicker
                    id={`business-branch-${location.id || location._key}`}
                    latitude={location.latitude}
                    longitude={location.longitude}
                    onChange={coordinates => setLocations(current => current.map(item => (item.id || item._key) === (location.id || location._key) ? { ...item, ...coordinates } : item))}
                    disabled={location.status !== 'active' || !canUpdate || isArchived}
                    label={`${location.branch_name || `ठिकाण ${index + 1}`} / Map location`}
                  />
                </div>
                {editing && location.status === 'active' && <div className="registry-property-action"><label>मालमत्ता लिंक<select value={location.property_action} onChange={event => updateLocationRow(location.id || location._key, 'property_action', event.target.value)}><option value="keep">सध्याची लिंक ठेवा {location.property_id ? `(#${location.property_id})` : '(नाही)'}</option><option value="remove">लिंक काढा</option><option value="replace">नवीन Property UID वापरा</option></select></label>{location.property_action === 'replace' && <Field label="Property UID" name={`loc-property-${index}`} value={location.property_uid} onChange={event => updateLocationRow(location.id || location._key, 'property_uid', event.target.value)} optional />}</div>}
                {location.status === 'active' && <footer>{editing ? <><button type="button" onClick={() => saveExistingLocation(location)} disabled={Boolean(saving) || !canUpdate || isArchived || roleChangeMode}>{saving === `location-${location.id}` ? <><span className="btn-spinner"></span> जतन…</> : 'बदल जतन'}</button><button type="button" className="danger" onClick={() => closeLocation(location)} disabled={Boolean(saving) || !canUpdate || isArchived || roleChangeMode}>ठिकाण बंद</button></> : <button type="button" className="danger" onClick={() => setLocations(current => current.filter((_, i) => i !== index))}>काढा</button>}</footer>}
              </article>)}
              {!locations.length && <p className="registry-empty">अद्याप व्यवसाय ठिकाण जोडलेले नाही.</p>}
            </div>
          </section>}

          {(isSinglePageMode || (editing && canManageUsers && currentStep === 5)) && <section className="registry-card">
            <div className="registry-section-heading">
              <span>5</span>
              <div>
                <h2>पोर्टल प्रवेश व परवानग्या</h2>
                <p>व्यवसाय मालक आणि अधिकृत व्यक्तींना कर घोषणा, पावत्या व पेमेंटसाठी दिलेले पोर्टल अधिकार.</p>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', marginBottom: '10px' }}>
                सक्रिय वापरकर्ता प्रवेश (Active Portal Grants):
              </div>
              {grants.length === 0 ? (
                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '14px', color: '#64748b', fontSize: '13px' }}>
                  सध्या कोणत्याही बाह्य व्यक्तीस स्वतंत्र प्रवेश मंजूर केलेला नाही. (नोंदणीकृत मालक थेट लॉगिन करू शकतात).
                </div>
              ) : (
                <div className="registry-editor-list grant-list">
                  {grants.map(grant => (
                    <article key={grant.grant_uid} className={grant.status !== 'active' ? 'historical' : ''} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div>
                          <strong style={{ fontSize: '14px', color: '#0f172a' }}>{grant.user?.full_name || `User #${grant.user?.id}`}</strong>
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>({grant.user?.email || grant.user?.phone || 'User'})</span>
                        </div>
                        <span style={{
                          background: grant.status === 'active' ? '#dcfce7' : '#f1f5f9',
                          color: grant.status === 'active' ? '#15803d' : '#64748b',
                          fontSize: '11px',
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: '12px',
                        }}>
                          {grant.status === 'active' ? '✓ सक्रिय (Active)' : grant.status}
                        </span>
                      </header>
                      <div style={{ fontSize: '12px', color: '#475569', marginBottom: '4px' }}>
                        <strong>परवानग्या:</strong> {(grant.scopes || []).join(' • ')}
                      </div>
                      {grant.party && (
                        <div style={{ fontSize: '12px', color: '#0284c7' }}>
                          संबंधित पक्ष: {grant.party.display_name || grant.party.legal_name}
                        </div>
                      )}
                      {grant.status === 'active' && (
                        <footer style={{ marginTop: '8px' }}>
                          <button type="button" className="danger" onClick={() => revokeGrant(grant)} disabled={Boolean(saving)}>
                            प्रवेश रद्द
                          </button>
                        </footer>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
              <button
                type="button"
                onClick={() => setShowAddGrant(prev => !prev)}
                style={{
                  padding: '8px 16px',
                  background: showAddGrant ? '#f1f5f9' : '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                {showAddGrant ? '▲ प्रवेश मंजुरी फॉर्म लपवा' : '+ इतर व्यक्तीस अतिरिक्त पोर्टल प्रवेश द्या (उदा. व्यवस्थापक / सीए)'}
              </button>

              {showAddGrant && (
                <div style={{ marginTop: '14px', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                  <div className="registry-picker-search">
                    <label>
                      <span className="sr-only">वापरकर्ता शोधा</span>
                      <input value={userQuery} onChange={event => setUserQuery(event.target.value)} placeholder="नाव, फोन किंवा ई-मेल" />
                    </label>
                    <button type="button" onClick={searchUsers} disabled={Boolean(saving)}>
                      {saving === 'user-search' ? <><span className="btn-spinner"></span> शोधत आहे…</> : 'मंजूर वापरकर्ता शोधा'}
                    </button>
                  </div>
                  {userResults.length > 0 && (
                    <div className="registry-picker-results">
                      {userResults.map(candidate => (
                        <button type="button" key={candidate.id} onClick={() => setGrantDraft(current => ({ ...current, user_id: candidate.id }))}>
                          <strong>{candidate.full_name}</strong>
                          <small>{candidate.email || candidate.phone} · User #{candidate.id}</small>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="registry-grid compact-grid grant-grid">
                    <Field label="निवडलेला User ID" name="grant-user" type="number" min="1" value={grantDraft.user_id} onChange={event => setGrantDraft({ ...grantDraft, user_id: event.target.value })} required />
                    <SelectField label="संबंधित पक्ष (ऐच्छिक)" name="grant-party" value={grantDraft.party_uid} onChange={event => setGrantDraft({ ...grantDraft, party_uid: event.target.value })} placeholder="पक्ष लिंक नाही" options={activeRecords(roles).filter(role => role.party?.party_uid).map(role => ({ value: role.party.party_uid, label: role.party.display_name || role.party.legal_name }))} optional />
                    <Field label="वैधता समाप्ती" name="grant-valid-to" type="datetime-local" value={grantDraft.valid_to} onChange={event => setGrantDraft({ ...grantDraft, valid_to: event.target.value })} optional />
                    <Field label="मंजुरी कारण" name="grant-reason" value={grantDraft.reason} onChange={event => setGrantDraft({ ...grantDraft, reason: event.target.value })} optional />
                    <fieldset className="registry-scope-field">
                      <legend>परवानग्या / Scopes</legend>
                      {GRANT_SCOPES.map(scope => (
                        <Checkbox key={scope} label={scope.replaceAll('_', ' ')} checked={grantDraft.scopes.includes(scope)} onChange={event => setGrantDraft(current => ({ ...current, scopes: event.target.checked ? [...new Set([...current.scopes, scope])] : current.scopes.filter(item => item !== scope) }))} />
                      ))}
                    </fieldset>
                  </div>
                  <button type="button" className="registry-add-button" onClick={createGrant} disabled={Boolean(saving) || isArchived} style={{ marginTop: '10px' }}>
                    {saving === 'grant' ? <><span className="btn-spinner"></span> मंजूर करत आहे…</> : 'प्रवेश मंजूर करा'}
                  </button>
                </div>
              )}
            </div>
          </section>}

          {(isSinglePageMode || (editing ? currentStep === 6 : currentStep === 5)) && <section className="registry-card review-card">
            <div className="registry-section-heading">
              <span>{editing ? '6' : '5'}</span>
              <div>
                <h2>{editing ? 'तयारी आणि पडताळणी कार्यप्रवाह' : 'कर आकारणी व पडताळणी कार्यप्रवाह (Tax & Approval)'}</h2>
                <p>{editing ? 'व्यवसाय स्थिती, पडताळणी आणि ऑडिट इतिहास' : 'नोंदणीसह थेट व्यवसाय सक्रिय व कर-पात्र करण्यासाठी खालील पर्याय निवडा.'}</p>
              </div>
            </div>

            {!editing && (
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600, color: '#0f172a', marginBottom: '14px' }}>
                  <input
                    type="checkbox"
                    checked={autoVerify}
                    onChange={e => setAutoVerify(e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#059669' }}
                  />
                  <span>✓ नोंदणीसह थेट व्यवसाय सक्रिय व कर-पात्र करा (Recommended)</span>
                </label>
                <p style={{ margin: '0 0 12px 28px', fontSize: '13px', color: '#64748b' }}>
                  हे निवडल्यास स्वतंत्र मंजुरीची वाट न पाहता व्यवसायाची नोंदणी तातडीने सक्रिय होईल आणि कर खाते सुरू होईल.
                </p>

                <div style={{ marginLeft: '28px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '6px' }}>
                    एकवेळ व्यवसाय परवाना नोंदणी शुल्क (One-Time Trade License Fee):
                  </span>
                  <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                    विद्यमान कार्यरत व्यवसायांची नोंदणी करताना <strong>'लागू नाही / विद्यमान व्यवसाय'</strong> निवडा. केवळ नवीन परवाना अर्जासाठी शुल्क लागू असल्यास रक्कम निवडा.
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    {[
                      { amount: 0, label: 'लागू नाही / विद्यमान व्यवसाय' },
                      { amount: 300, label: '₹३००' },
                      { amount: 500, label: '₹५००' },
                      { amount: 1000, label: '₹१,०००' },
                      { amount: 2000, label: '₹२,०००' },
                    ].map(tier => (
                      <button
                        key={tier.amount}
                        type="button"
                        onClick={() => { setTaxAmount(String(tier.amount)); setIsCustomTax(false); }}
                        style={{
                          padding: '7px 16px',
                          borderRadius: '20px',
                          border: String(taxAmount) === String(tier.amount) && !isCustomTax ? '2px solid #059669' : '1px solid #cbd5e1',
                          background: String(taxAmount) === String(tier.amount) && !isCustomTax ? '#ecfdf5' : '#fff',
                          color: String(taxAmount) === String(tier.amount) && !isCustomTax ? '#059669' : '#334155',
                          fontWeight: String(taxAmount) === String(tier.amount) && !isCustomTax ? 700 : 500,
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        {tier.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setIsCustomTax(true)}
                      style={{
                        padding: '7px 16px',
                        borderRadius: '20px',
                        border: isCustomTax ? '2px solid #059669' : '1px solid #cbd5e1',
                        background: isCustomTax ? '#ecfdf5' : '#fff',
                        color: isCustomTax ? '#059669' : '#334155',
                        fontWeight: isCustomTax ? 700 : 500,
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      इतर रक्कम
                    </button>
                  </div>

                  {isCustomTax && (
                    <div style={{ marginBottom: '14px' }}>
                      <input
                        type="number"
                        min="0"
                        value={taxAmount}
                        onChange={e => setTaxAmount(e.target.value)}
                        placeholder="परवाना शुल्क रक्कम टाका (उदा. ७५०)"
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '240px', fontSize: '14px' }}
                      />
                    </div>
                  )}

                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 16px', marginTop: '12px' }}>
                    <div style={{ fontWeight: 600, color: '#1e40af', fontSize: '13px', marginBottom: '4px' }}>
                      ℹ कर आकारणी कशी कार्य करते? (How Tax Calculation Works)
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#1e3a8a', lineHeight: '1.6' }}>
                      <li><strong>वार्षिक कर (Annual Tax)</strong>: हा कर दरवर्षी ग्रामपंचायतीच्या <em>कर इंजिनमधील मंजूर नियमांनुसार (Approved Tax Rules)</em> आपोआप आकारला जातो.</li>
                      <li><strong>परवाना शुल्क (License Fee)</strong>: हे शुल्क केवळ नवीन व्यवसाय सुरू करताना एकदाच (One-Time) आकारले जाणारे नोंदणी शुल्क आहे. ते वार्षिक करात जोडले जात नाही.</li>
                      <li><strong>कर आकारणी पात्रता</strong>: येथे 'लागू नाही' किंवा ₹० निवडल्याने व्यवसाय करमुक्त होत नाही; कर इंजिनमधील नियमांनुसार त्याची वार्षिक कर मागणी नियमितपणे निर्माण होईल.</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {editing && (
              <>
                <div className={`registry-readiness ${readiness.ready ? 'ready' : 'review'}`}>
                  <h3>{readiness.ready ? '✓ भूमिका आणि ठिकाणांची कर-पात्रता पूर्ण आहे' : '⚠ पुढील बाबी पूर्ण करा'}</h3>
                  {!readiness.ready && <ul>{readiness.missing.map(item => <li key={item}>{READINESS_LABELS[item] || item}</li>)}</ul>}
                </div>
                <label className="registry-field wide" htmlFor="review-reason">
                  <span>Review कारण / नोंद (ऐच्छिक)</span>
                  <textarea id="review-reason" rows="2" value={reviewReason} onChange={event => setReviewReason(event.target.value)} placeholder="प्रशासकीय पडताळणी व मंजुरी" />
                </label>
              </>
            )}

            <div className="registry-workflow-actions">
              {!editing && (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {autoVerify && (
                    <button
                      type="button"
                      className="success"
                      style={{ background: '#059669', color: '#fff', padding: '12px 24px', fontSize: '15px', fontWeight: 700, borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                      onClick={(e) => saveCore(e, true)}
                      disabled={Boolean(saving)}
                    >
                      {saving === 'core' ? <><span className="btn-spinner"></span> सक्रिय होत आहे…</> : '✓ व्यवसाय जतन करून सक्रिय करा'}
                    </button>
                  )}
                  <button
                    type="button"
                    style={{ padding: '12px 20px', fontSize: '14px', borderRadius: '8px', border: '1px solid #94a3b8', background: '#f8fafc', color: '#1e293b', fontWeight: 600, cursor: 'pointer' }}
                    onClick={(e) => saveCore(e, false)}
                    disabled={Boolean(saving)}
                  >
                    {saving === 'core' ? <><span className="btn-spinner"></span> जतन होत आहे…</> : 'तपासणीसाठी पाठवा (Pending)'}
                  </button>
                </div>
              )}
              {editing && ['draft', 'pending'].includes(business?.verification_status) && canVerify && (
                <button
                  type="button"
                  className="success"
                  style={{ background: '#059669', color: '#fff', padding: '10px 20px', fontSize: '15px', fontWeight: 700 }}
                  onClick={() => transition('verify')}
                  disabled={Boolean(saving)}
                >
                  {saving === 'verify' ? <><span className="btn-spinner"></span> मंजूर होत आहे…</> : '✓ आता मंजूर व सक्रिय करा'}
                </button>
              )}
              {editing && ['draft', 'rejected', 'suspended'].includes(business?.verification_status) && canUpdate && (
                <button type="button" onClick={() => transition('submit')} disabled={Boolean(saving)}>
                  {saving === 'submit' ? <><span className="btn-spinner"></span> पाठवत आहे…</> : 'तपासणीसाठी पाठवा (Pending)'}
                </button>
              )}
              {editing && ['draft', 'pending'].includes(business?.verification_status) && canVerify && (
                <button type="button" className="warning" onClick={() => transition('reject')} disabled={Boolean(saving)}>
                  {saving === 'reject' ? <><span className="btn-spinner"></span> नाकारत आहे…</> : 'नाकारा'}
                </button>
              )}
              {editing && business?.verification_status !== 'archived' && canArchive && (
                <button type="button" className="danger" onClick={() => transition('archive')} disabled={Boolean(saving)}>
                  {saving === 'archive' ? <><span className="btn-spinner"></span> संग्रहित करत आहे…</> : 'इतिहासासह संग्रहित करा'}
                </button>
              )}
            </div>
          </section>}

          {!isSinglePageMode && (
            <div className="registry-step-actions">
              <button
                type="button"
                className="btn-prev"
                disabled={currentStep === 1}
                onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              >
                ← मागील पायरी
              </button>

              <button
                type="submit"
                className="btn-draft"
                disabled={!canUpdate || Boolean(saving) || isArchived || roleChangeMode}
              >
                {saving === 'core' ? <><span className="btn-spinner"></span> जतन होत आहे…</> : roleChangeMode ? 'Finish staged role change first' : editing ? 'माहिती जतन करा' : 'मसुदा जतन करा'}
              </button>

              {currentStep === steps.length && !editing ? (
                <button
                  type="button"
                  className="btn-primary"
                  style={{ background: '#059669', color: '#fff', padding: '10px 24px', fontSize: '15px', fontWeight: 700, borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                  disabled={!canUpdate || Boolean(saving) || isArchived || roleChangeMode}
                  onClick={(e) => saveCore(e, true)}
                >
                  {saving === 'core' ? <><span className="btn-spinner"></span> सक्रिय होत आहे…</> : '✓ व्यवसाय जतन व सक्रिय करा'}
                </button>
              ) : null}

              {currentStep < steps.length ? (
                <button
                  type="button"
                  className="btn-next"
                  onClick={handleNextStep}
                >
                  पुढील पायरी →
                </button>
              ) : null}
            </div>
          )}

          {isSinglePageMode && (
            <div className="registry-sticky-save">
              {!editing && (
                <button
                  type="button"
                  className="btn-primary"
                  style={{ background: '#059669', color: '#fff' }}
                  disabled={!canUpdate || Boolean(saving) || isArchived || roleChangeMode}
                  onClick={(e) => saveCore(e, true)}
                >
                  {saving === 'core' ? <><span className="btn-spinner"></span> सक्रिय होत आहे…</> : '✓ व्यवसाय जतन व सक्रिय करा'}
                </button>
              )}
              <button type="submit" className="btn-primary" disabled={!canUpdate || Boolean(saving) || isArchived || roleChangeMode}>{saving === 'core' ? <><span className="btn-spinner"></span> जतन होत आहे…</> : roleChangeMode ? 'Finish staged role change first' : editing ? 'मुख्य माहिती जतन करा' : 'मसुदा जतन करा'}</button>
              <button type="button" className="btn-secondary" onClick={() => navigate(`/admin/${tenantSlug}/businesses`)}>रद्द / सूची</button>
            </div>
          )}
        </form>

        {editing && canViewTaxDeclarations && (isSinglePageMode || currentStep === 5) && (
          <BusinessTaxDeclarations
            tenantSlug={tenantSlug}
            businessUid={business?.business_uid}
            canManage={canManageTaxDeclarations}
            canApprove={canApproveTaxDeclarations}
            onNotice={setMessage}
          />
        )}
      </div>
    );
  }
